import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { DeferBuyNowModal } from '../../components/defer-buy-now-modal/defer-buy-now-modal';
import { DdpTermsAndConditionsModal } from '../../components/modals/ddp-terms-and-conditions-modal/ddp-terms-and-conditions-modal';
import { PhoneNumberCaptureModal } from '../../components/phone-number-capture-modal/phone-number-capture-modal';
import { emptyObject } from 'ui-components/default-props/empty';
import { PaymentUpdateModalWrapper } from '../../financial-services-ui/payment-update/index';
import { useModal } from '../../hooks/use-modal';
import { NewLineupPromotionalMigrationLoader } from '../../pages/settings/payment-and-subscription/components/migrate-plan-modal/new-lineup-promotional-migration-loader';
import { useGetEmailMarketingHandoffModalDetails } from '../../pages/settings/payment-and-subscription/components/migrate-plan-modal/use-get-email-marketing-handoff-modal-details';
import { usePaymentAndSubscriptionMegaContext } from '../../pages/settings/payment-and-subscription/context';
import { ManageSubscriptionDrawerContextProvider } from '../../pages/settings/payment-and-subscription/context/manage-subscription-drawer-context';
import { WalletRegistrationModal } from '../../wallet/modals/wallet-registration';
import { WalletUpdateModal } from '../../wallet/modals/wallet-update';
import { PAYMENT_TAB } from '../../wallet/utilities/constants';
import { Segment } from '../../services/analytics-services/segment';
import { PHONE_CAPTURE_MODAL_OPENED } from '../../constants/analytics-events';

export const GlobalModalLaunchContext = createContext(null);
export const useGlobalModalLaunchContext = () => useContext(GlobalModalLaunchContext);

// note that each modal section _can_ be extracted to its own hooks that provide the necessary context if things get busy, which can all be consumed here
export const GlobalModalLaunchContextProvider = ({ children }) => {
  const { setSelectedPlanId, setDesiredNumberOfAdditionalUsers } = usePaymentAndSubscriptionMegaContext();

  // WalletRegistrationModal
  const [currentWalletRegistrationStepId, setCurrentWalletRegistrationStepId] = useState(PAYMENT_TAB);
  const [walletRegistrationProps, setWalletRegistrationProps] = useState();
  const { close: closeWalletRegistration, isOpen: isWalletRegistrationOpen, open: openWalletRegistration } = useModal();
  const handleOpenWalletRegistration = useCallback(
    (newWalletRegistrationProps, initialStepId) => {
      if (initialStepId) {
        // allows for setting of initial step id, and then takes ownership of anything further
        setCurrentWalletRegistrationStepId(initialStepId);
      }
      setWalletRegistrationProps(newWalletRegistrationProps);
      openWalletRegistration();
    },
    [openWalletRegistration],
  );
  const handleCloseWalletRegistration = useCallback(() => {
    // possibly allow other callbacks to pass in here, unsure yet of pattern
    setWalletRegistrationProps(undefined);
    setCurrentWalletRegistrationStepId(PAYMENT_TAB);
    closeWalletRegistration();
  }, [closeWalletRegistration, setWalletRegistrationProps]);

  // WalletUpdateModal
  const [walletUpdateProps, setWalletUpdateProps] = useState();
  const { close: closeWalletUpdateModal, isOpen: isWalletUpdateModalOpen, open: openWalletUpdateModal } = useModal();
  const handleOpenWalletUpdateModal = useCallback(
    (newWalletUpdateProps) => {
      setWalletUpdateProps(newWalletUpdateProps);
      openWalletUpdateModal();
    },
    [openWalletUpdateModal, setWalletUpdateProps],
  );
  const handleCloseWalletUpdateModal = useCallback(() => {
    // possibly allow other callbacks to pass in here, unsure yet of pattern
    setWalletUpdateProps(undefined);
    closeWalletUpdateModal();
  }, [closeWalletUpdateModal, setWalletUpdateProps]);

  // BillingInfoModal
  const [billingInfoModalProps, setBillingInfoModalProps] = useState();
  const [isBillingInfoModalOpen, setIsBillingInfoModalOpen] = useState(false);
  const handleOpenBillingInfoModal = useCallback(
    (props) => {
      setBillingInfoModalProps(props);
      setIsBillingInfoModalOpen(true);
    },
    [setIsBillingInfoModalOpen],
  );
  const handleCloseBillingInfoModal = useCallback(() => {
    setBillingInfoModalProps(undefined);
    setIsBillingInfoModalOpen(false);
  }, [setIsBillingInfoModalOpen]);

  // ManageSubscriptionDrawer
  const [manageSubscriptionDrawerProps, setManageSubscriptionDrawerProps] = useState(emptyObject);
  const { close: closeManageSubscriptionDrawer, isOpen: isManageSubscriptionDrawerOpen, open: openManageSubscriptionDrawer } = useModal();
  const handleOpenManageSubscriptionDrawer = useCallback(
    (props = emptyObject) => {
      setSelectedPlanId((prev) => props?.selectedPlanId || prev);
      setDesiredNumberOfAdditionalUsers((prev) => props?.desiredNumberOfAdditionalUsers ?? prev);
      setManageSubscriptionDrawerProps(props);

      setTimeout(() => {
        // ensure that the drawer is open after the props are set
        openManageSubscriptionDrawer();
      }, 0);
    },
    [openManageSubscriptionDrawer, setDesiredNumberOfAdditionalUsers, setSelectedPlanId],
  );
  const handleCloseManageSubscriptionDrawer = useCallback(() => {
    setManageSubscriptionDrawerProps(emptyObject);
    closeManageSubscriptionDrawer();
  }, [closeManageSubscriptionDrawer, setManageSubscriptionDrawerProps]);

  // DeferBuyNowModal
  const { close: closeDeferBuyNowModal, isOpen: isDeferBuyNowModalOpen, open: openDeferBuyNowModal } = useModal();

  // DdpTermsAndConditionsModal (for direct access to terms)
  const { close: closeDdpTermsModal, isOpen: isDdpTermsModalOpen, open: openDdpTermsModal } = useModal();
  const ddpTermsModalClosedCallbackRef = useRef();

  const handleCloseDdpTermsModal = useCallback(() => {
    if (ddpTermsModalClosedCallbackRef.current) {
      ddpTermsModalClosedCallbackRef.current();
    }
    closeDdpTermsModal();
  }, [closeDdpTermsModal]);

  const registerDdpTermsModalClosedCallback = useCallback((callback) => {
    ddpTermsModalClosedCallbackRef.current = callback;
  }, []);

  // PhoneNumberCaptureModal
  const [phoneCaptureModalProps, setPhoneCaptureModalProps] = useState();
  const { close: closePhoneCaptureModal, isOpen: isPhoneCaptureModalOpen, open: openPhoneCaptureModal } = useModal();
  const handleOpenPhoneCaptureModal = useCallback(
    (source) => {
      setPhoneCaptureModalProps({ source });
      Segment.trackEvent(PHONE_CAPTURE_MODAL_OPENED, { source });
      openPhoneCaptureModal();
    },
    [openPhoneCaptureModal],
  );
  const handleClosePhoneCaptureModal = useCallback(() => {
    setPhoneCaptureModalProps(undefined);
    closePhoneCaptureModal();
  }, [closePhoneCaptureModal]);

  const { shouldShowMarketingHandoffModal, hideMarketingHandoffModal } = useGetEmailMarketingHandoffModalDetails();

  // memo value
  const value = useMemo(
    () => ({
      currentWalletRegistrationStepId,
      setCurrentWalletRegistrationStepId,
      openWalletRegistration: handleOpenWalletRegistration,
      closeWalletRegistration: handleCloseWalletRegistration,
      openWalletUpdate: handleOpenWalletUpdateModal,
      closeWalletUpdate: handleCloseWalletUpdateModal,
      openBillingInfoModal: handleOpenBillingInfoModal,
      closeBillingInfoModal: handleCloseBillingInfoModal,
      openManageSubscriptionDrawer: handleOpenManageSubscriptionDrawer,
      closeManageSubscriptionDrawer: handleCloseManageSubscriptionDrawer,
      openDeferBuyNowModal,
      closeDeferBuyNowModal,
      openDdpTermsModal,
      closeDdpTermsModal: handleCloseDdpTermsModal,
      registerDdpTermsModalClosedCallback,
      openPhoneCaptureModal: handleOpenPhoneCaptureModal,
      closePhoneCaptureModal: handleClosePhoneCaptureModal,

      // rather than render here, allow this to be used in the consuming component on the specific redirect page
      // so that the component is guaranteed to be within the right context(s)
      isBillingInfoModalOpen,
    }),
    [
      currentWalletRegistrationStepId,
      handleOpenWalletRegistration,
      handleCloseWalletRegistration,
      handleOpenWalletUpdateModal,
      handleCloseWalletUpdateModal,
      handleOpenBillingInfoModal,
      handleCloseBillingInfoModal,
      handleOpenManageSubscriptionDrawer,
      handleCloseManageSubscriptionDrawer,
      openDeferBuyNowModal,
      closeDeferBuyNowModal,
      openDdpTermsModal,
      handleCloseDdpTermsModal,
      registerDdpTermsModalClosedCallback,
      handleOpenPhoneCaptureModal,
      handleClosePhoneCaptureModal,
      isBillingInfoModalOpen,
    ],
  );

  return (
    <GlobalModalLaunchContext.Provider value={value}>
      {children}
      {isWalletRegistrationOpen && walletRegistrationProps ? <WalletRegistrationModal {...walletRegistrationProps} /> : null}
      {isWalletUpdateModalOpen && walletUpdateProps ? <WalletUpdateModal {...walletUpdateProps} /> : null}
      {isBillingInfoModalOpen ? <PaymentUpdateModalWrapper onClose={handleCloseBillingInfoModal} {...billingInfoModalProps} /> : null}
      {isManageSubscriptionDrawerOpen ? (
        <ManageSubscriptionDrawerContextProvider
          initialIsManageModalOpen
          onClose={handleCloseManageSubscriptionDrawer}
          {...manageSubscriptionDrawerProps}
        />
      ) : null}
      {isDeferBuyNowModalOpen ? <DeferBuyNowModal onClose={closeDeferBuyNowModal} /> : null}
      {isDdpTermsModalOpen ? <DdpTermsAndConditionsModal onClose={handleCloseDdpTermsModal} /> : null}
      {isPhoneCaptureModalOpen && phoneCaptureModalProps ? (
        <PhoneNumberCaptureModal onClose={handleClosePhoneCaptureModal} {...phoneCaptureModalProps} />
      ) : null}
      {shouldShowMarketingHandoffModal && (
        <NewLineupPromotionalMigrationLoader hideMarketingHandoffModal={hideMarketingHandoffModal} shouldStartWithEmailHandoff />
      )}
    </GlobalModalLaunchContext.Provider>
  );
};
